import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { FiAward, FiGift } from "react-icons/fi";

import { GET_HOMEPAGE_COMPETITIONS } from "MVVM/GraphQL/competitions";
import { formatNumber } from "MVVM/Utilities/Clip";
import { ICompetition } from "MVVM/Views/Competitions/@types";
import {
  COMPETITION_PLACEHOLDER_BANNER,
  getCompetitionPhase,
  getPrizeLabel,
} from "MVVM/Views/Competitions/utils";
import {
  WidgetCard,
  WidgetHeader,
  WidgetTag,
  WidgetTitle,
} from "../styledComponents";
import {
  Banner,
  CompetitionLink,
  LiveDot,
  Meta,
  Prize,
  TagWithDot,
  Title,
} from "./styledComponents";

const POOL_SIZE = 10;

const PHASE_TAG: Record<string, string> = {
  active: "Live",
  upcoming: "Soon",
  ended: "Ended",
};

export const RecentCompetitionWidget = () => {
  const { data, loading, error } = useQuery(GET_HOMEPAGE_COMPETITIONS, {
    variables: { first: POOL_SIZE },
    fetchPolicy: "cache-and-network",
  });

  // "Most recent" means the one worth surfacing right now: whatever is live,
  // else whatever starts soonest, else the one that just wrapped. Phase is
  // re-derived client-side because a competition can flip between the server
  // responding and this render.
  const competition = useMemo<ICompetition | null>(() => {
    const nodes = (field: "active" | "upcoming" | "ended") =>
      ((data?.[field]?.nodes ?? []) as ICompetition[]).filter(
        (c) => getCompetitionPhase(c) === field,
      );

    const endingSoonest = [...nodes("active")].sort(
      (a, b) => new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime(),
    );
    const startingSoonest = [...nodes("upcoming")].sort(
      (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
    );
    const justEnded = [...nodes("ended")].sort(
      (a, b) => new Date(b.endsAt).getTime() - new Date(a.endsAt).getTime(),
    );

    // ensure sponsored competitions always take priority
    const byPhase = [...endingSoonest, ...startingSoonest, ...justEnded];
    return byPhase.find((c) => c.sponsored) ?? byPhase[0] ?? null;
  }, [data]);

  if (loading || error || !competition) return null;

  const phase = getCompetitionPhase(competition);

  return (
    <WidgetCard>
      <WidgetHeader>
        <WidgetTitle>
          <FiAward size={15} />
          Competition
        </WidgetTitle>
        <TagWithDot>
          <WidgetTag>{PHASE_TAG[phase]}</WidgetTag>
          {phase === "active" && <LiveDot />}
        </TagWithDot>
      </WidgetHeader>

      <CompetitionLink to={`/competitions/${competition._id}`}>
        <Banner
          $src={competition.bannerGraphic || COMPETITION_PLACEHOLDER_BANNER}
        />
        <Title>{competition.title}</Title>
        <Prize>
          <FiGift size={13} />
          {getPrizeLabel(competition.prizes)}
        </Prize>
        <Meta>{formatNumber(competition.participantCount ?? 0)} entered</Meta>
      </CompetitionLink>
    </WidgetCard>
  );
};
